import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { PageBanner } from "../../components";
import CartItemCard from "../../components/CartItemCard/CartItemCard";
import { backend } from "../../api";
import { ToastContainer, toast } from "react-toastify";
import { Box, Button, Container, Grid, makeStyles } from "@material-ui/core";
import theme from "../../theme";
import { Link } from "react-router-dom";

import { useMyContext } from "../../contextApi/context";
const useStyle = makeStyles((theme) => ({
  accordion: {
    backgroundColor: theme.palette.darkGray,
  },
  directoryName: {
    margin: 0,
    fontWeight: 600,
    textTransform: "capitalize",
  },
  headerNew: {
    fontSize: 22,
    fontWeight: "500",
    color: theme?.palette.white,
    textAlign: "start",
  },
  heightFull: {
    height: "100%",
  },
  summaryWrap: {
    marginTop: 30,
    position: "sticky",
    top: 100,
    zIndex: 1000,
    border: `1px solid ${theme?.palette.primary.dark}`,
    padding: 10,
    borderRadius: 8,
  },
  details: {
    backgroundColor: theme.palette.secondary.dark,
  },
  wrapPrice: {
    display: "flex",
    justifyContent: "space-between",
  },
  checkoutBtn: {
    width: "100%",
  },
  submitBtn: {
    marginTop: 14,
    color: theme.palette.secondary.light,
    backgroundColor: theme.palette.primary.main,
    width: "98%",
    borderRadius: 13,
    padding: "8px 0",
    "&:hover": {
      backgroundColor: theme.palette.secondary.light,
      color: "white",
    },
  },
  line: {
    marginTop: 10,
    borderBottom: `1px solid ${theme.palette.primary.dark}`,
    width: "100%",
    marginBottom: 14,
  },
}));

export default function ShoppingCart() {
  const classes = useStyle();
  const { taxPercentage, setTaxPercentage, cartItems, setCartItems } = useMyContext();
  
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalTaxes, setTotalTaxes] = useState(0);

  useEffect(() => {
    fetchCartItems();
    getTaxes();
  }, []);

  useEffect(() => {
    calculateTotalPricesAndTaxes();
  }, [cartItems, taxPercentage]);

  const fetchCartItems = async () => {
    try {
      const response = await backend.get("/shopCart");
      setCartItems(response?.data?.data);
    } catch (error) {
      console.error("Error fetching cart items:", error);
    }
  };

  const calculateItemPrice = (item) => {
    const bookOrderType = item?.bookOrderType;
    const quantity = item?.quantity || 1; // Assuming quantity defaults to 1 if not provided

    if (bookOrderType === "HARDCOPY") {
      return (item?.bookId?.price?.hardcover_price || 0) * quantity;
    } else if (bookOrderType === "BOTH") {
      return (
        ((item?.bookId?.price?.both_price || 0) ) * quantity
      );
    } else if (item?.cartItemType === "ARTICLE") {
      return (
        (item?.articleId?.per_page_price || 0) *
        (item?.articleId?.number_of_pages || 0) *
        quantity
      );
    } else {
      return (item?.bookId?.price?.digital_price || 0) * quantity;
    }
  };

  const calculateTax = (item) => {
    const bookOrderType = item?.bookOrderType;
    const quantity = item?.quantity || 1;

    if (bookOrderType === "HARDCOPY") {
      return (item?.bookId?.price?.hardcover_price || 0) * (taxPercentage / 100) * quantity;
    } else if (bookOrderType === "BOTH") {
      const hardcoverPrice = item?.bookId?.price?.hardcover_price || 0;
      const digitalPrice = item?.bookId?.price?.digital_price || 0;
      return ((hardcoverPrice + digitalPrice ) * (taxPercentage / 100)) * quantity;
    } else if (item?.cartItemType === "ARTICLE") {
      const perPagePrice = item?.articleId?.per_page_price || 0;
      const numberOfPages = item?.articleId?.number_of_pages || 0;
      return (perPagePrice * numberOfPages) * (taxPercentage / 100) * quantity;
    } else {
      return (item?.bookId?.price?.digital_price || 0) * (taxPercentage / 100) * quantity;
    }
  };

  const calculateTotalPricesAndTaxes = () => {
    const totalPrices = cartItems.reduce((acc, item) => acc + calculateItemPrice(item), 0);
    const totalTaxAmount = totalPrices * (taxPercentage / 100);
    setTotalPrice(totalPrices);
    setTotalTaxes(totalTaxAmount);
  };

  const getTaxes = async () => {
    try {
      const data = await backend.get("/amount");
      setTaxPercentage(data?.data?.data[0].tax);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleRemoveFromCart = async (itemId) => {
    try {
      await backend.delete(`/shopCart/${itemId}`);
      setCartItems(cartItems.filter((item) => item._id !== itemId));
      fetchCartItems();
      toast.success("Successfully Deleted");
    } catch (error) {
      console.error("Error removing item from cart:", error);
      toast.error(error.response ? error.response.data.message : error.message);
    }
  };

  return (
    <div className={classes.accordion}>
      <Helmet>
        <title>Shopping Cart</title>
      </Helmet>
      <PageBanner image={"/assets/images/bg4.jpg"} title={"Shopping Cart"} />
      <ToastContainer />
      <Box className={classes.heightFull} py={5}>
        <Container>
          <Grid container spacing={3}>
            <Grid style={{ margin: "0 auto", textAlign: "center" }} item xs={12} md={10} lg={10}>
              <h2 className={classes.headerNew}>Your Cart ({cartItems?.length})</h2>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  {cartItems.length > 0 ? (
                    <Box my={3}>
                      <Grid container spacing={6}>
                        <Grid item xs={12}>
                          {cartItems.map((item) => (
                            <CartItemCard
                              key={item._id}
                              item={item}
                              onRemove={handleRemoveFromCart}
                              taxAmount={calculateTax(item)}
                              quantity={item?.quantity}
                              calculateItemPrice={calculateItemPrice}
                            />
                          ))}
                        </Grid>
                      </Grid>
                    </Box>
                  ) : (
                    <Box textAlign="center">
                      <p>No items in the cart</p>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} md={4}>
                  {cartItems?.length > 0 ? (
                    <div className={classes.summaryWrap}>
                      <p
                        style={{
                          margin: 0,
                          color: theme?.palette.secondary.dark,
                          textAlign: "start",
                          fontWeight: "500",
                          fontSize: 20,
                        }}
                      >
                        ORDER SUMMARY
                      </p>
                      <div className={classes.line}></div>
                      <div className={classes.wrapPrice}>
                        <p
                          style={{
                            margin: 0,
                            color: theme?.palette.secondary.dark,
                            textAlign: "start",
                            fontWeight: "500",
                            fontSize: 14,
                          }}
                        >
                          Tax(%)
                        </p>
                        <p
                          style={{
                            margin: 0,
                            color: theme?.palette.secondary.dark,
                            textAlign: "start",
                            fontWeight: "500",
                            fontSize: 14,
                          }}
                        >
                          {taxPercentage}%
                        </p>
                      </div>
                      <div className={classes.wrapPrice} style={{ marginTop: "20px" }}>
                        <p
                          style={{
                            margin: 0,
                            color: theme?.palette.secondary.dark,
                            textAlign: "start",
                            fontWeight: "500",
                            fontSize: 14,
                          }}
                        >
                          Sub Total
                        </p>
                        <p
                          style={{
                            margin: 0,
                            color: theme?.palette.secondary.dark,
                            textAlign: "start",
                            fontWeight: "500",
                            fontSize: 14,
                          }}
                        >
                          ${totalPrice.toFixed(2)}
                        </p>
                      </div>
                      <div className={classes.wrapPrice} style={{ marginTop: "20px" }}>
                        <p
                          style={{
                            margin: 0,
                            color: theme?.palette.secondary.dark,
                            textAlign: "start",
                            fontWeight: "500",
                            fontSize: 14,
                          }}
                        >
                          Total Tax
                        </p>
                        <p
                          style={{
                            margin: 0,
                            color: theme?.palette.secondary.dark,
                            textAlign: "start",
                            fontWeight: "500",
                            fontSize: 14,
                          }}
                        >
                          ${totalTaxes.toFixed(2)}
                        </p>
                      </div>
                      <div className={classes.wrapPrice} style={{ marginTop: "20px" }}>
                        <p
                          style={{
                            margin: 0,
                            color: theme?.palette.secondary.dark,
                            textAlign: "start",
                            fontWeight: "500",
                            fontSize: 14,
                          }}
                        >
                          Total
                        </p>
                        <p
                          style={{
                            margin: 0,
                            color: theme?.palette.secondary.dark,
                            textAlign: "start",
                            fontWeight: "500",
                            fontSize: 14,
                          }}
                        >
                          ${(totalPrice + totalTaxes).toFixed(2)}
                        </p>
                      </div>
                      <Button
                        className={classes.submitBtn}
                        variant="contained"
                        color="primary"
                        component={Link}
                        to={{
                          pathname: "/cart-checkout",
                          state: { totalPrice: totalPrice + totalTaxes },
                        }}
                      >
                        Checkout
                      </Button>
                    </div>
                  ) : (
                    <p
                      style={{
                        margin: 0,
                        color: theme?.palette.secondary.dark,
                        textAlign: "start",
                        fontWeight: "500",
                        fontSize: 20,
                      }}
                    >
                      Cart is Empty.
                    </p>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </div>
  );
}
